import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewPlaceMutation } from '../../api/placesApiSlice';
import { paikkaFields } from './adminUtils/fields';
import { selectAllRegionNames, selectMunicipalitiesByRegion } from '../../api/regionsApiSlice';
import { selectAllCenterNames } from '../../api/centersApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function PlacesList({ places, handleDeletePlace, handleUpdatePlace, fetchData, onSuccess, onError }) {
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [newPlaceData, setNewPlaceData] = useState({
        keskus: '',
        nimi: '',
        osoite: '',
        maakunta: '',
        kunta: '',
        postitoimipaikka: '',
        postinumero: '',
    });
    const [addPlaceData, setAddPlaceData] = useState({
        keskus: '',
        nimi: '',
        osoite: '',
        maakunta: '',
        kunta: '',
        postitoimipaikka: '',
        postinumero: '',
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [addNewPlace, { isLoading }] = useAddNewPlaceMutation();
    const centers = useSelector(selectAllCenterNames);
    const fields = paikkaFields;
    const regionNames = useSelector(selectAllRegionNames);
    const regions = useSelector(selectMunicipalitiesByRegion);

    const municipalitiesByRegion = regions.reduce((acc, region) => {
        const municipalities = region.kunnat.map((kunta) => kunta.nimi);
        return { ...acc, [region.nimi]: municipalities };
    }, {});

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredPlaces = Array.isArray(places)
        ? places.filter((place) =>
            place.nimi.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
    const totalPages = Math.ceil(filteredPlaces.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedPlaces = filteredPlaces.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (place) => {
        setShowAddForm(false);
        if (selectedPlace?.id === place.id) {
            setSelectedPlace(null);
            setNewPlaceData({ keskus: '', nimi: '', osoite: '', maakunta: '', kunta: '', postitoimipaikka: '', postinumero: '' });
        } else {
            setSelectedPlace(place);
            setNewPlaceData(place);
        }
    };

    const handleAddClick = () => {
        setSelectedPlace(null);
        setNewPlaceData({ keskus: '', nimi: '', osoite: '', maakunta: '', kunta: '', postitoimipaikka: '', postinumero: '' });
        setShowAddForm(!showAddForm);
    };

    const handleLisääPlace = async (place) => {
        try {
            const result = await addNewPlace(place);
            if (result.error) onError('Paikan luominen epäonnistui');
            if (!result.error) onSuccess('Paikan luominen onnistui.', 'paikat', result.data.id);
            setAddPlaceData({ keskus: '', nimi: '', osoite: '', maakunta: '', kunta: '', postitoimipaikka: '', postinumero: '' });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Paikan luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Paikat
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Places List */}
                {displayedPlaces.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei paikkoja näytettäväksi.
                    </Typography>
                ) : (
                    displayedPlaces.map((place) => {
                        const isSelected = selectedPlace?.id === place.id;
                        const locationParts = [place.osoite, place.postinumero, place.postitoimipaikka].filter(Boolean);

                        return (
                            <Box key={place.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(place)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                                            {place.nimi}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {locationParts.join(', ') || 'Ei osoitetietoja'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {place.keskus && <Chip size="small" label={place.keskus} />}
                                            {place.maakunta && <Chip size="small" variant="outlined" label={place.maakunta} />}
                                            {place.kunta && <Chip size="small" variant="outlined" label={place.kunta} />}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeletePlace(event, place);
                                            if (selectedPlace?.id === place.id) {
                                                setSelectedPlace(null);
                                                setNewPlaceData({ keskus: '', nimi: '', osoite: '', maakunta: '', kunta: '', postitoimipaikka: '', postinumero: '' });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa paikkaa
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                {fields.map((fieldName) =>
                                                    fieldName === 'keskus' ? (
                                                        <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                            <InputLabel id="keskus-label">Keskus</InputLabel>
                                                            <Select
                                                                labelId="keskusId"
                                                                value={newPlaceData.keskus}
                                                                label="Keskus"
                                                                onChange={(event) =>
                                                                    setNewPlaceData((prev) => ({ ...prev, keskus: event.target.value }))
                                                                }
                                                            >
                                                                {centers.map((center) => (
                                                                    <MenuItem key={center} value={center}>
                                                                        {center}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : fieldName === 'maakunta' ? (
                                                        <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                            <InputLabel id="maakunta-label">Maakunta</InputLabel>
                                                            <Select
                                                                labelId="maakuntaId"
                                                                value={newPlaceData.maakunta}
                                                                label="Maakunta"
                                                                onChange={(event) =>
                                                                    setNewPlaceData((prev) => ({ ...prev, maakunta: event.target.value }))
                                                                }
                                                            >
                                                                {regionNames.map((region) => (
                                                                    <MenuItem key={region} value={region}>
                                                                        {region}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : fieldName === 'kunta' ? (
                                                        <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                            <InputLabel id="kunta-label">Kunta</InputLabel>
                                                            <Select
                                                                labelId="kuntaId"
                                                                value={newPlaceData.kunta}
                                                                label="Kunta"
                                                                onChange={(event) =>
                                                                    setNewPlaceData((prev) => ({ ...prev, kunta: event.target.value }))
                                                                }
                                                            >
                                                                {municipalitiesByRegion[newPlaceData.maakunta]?.map((kunta) => (
                                                                    <MenuItem key={kunta} value={kunta}>
                                                                        {kunta}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <FormTextField
                                                            key={fieldName}
                                                            fieldName={fieldName}
                                                            label={fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
                                                            multiline
                                                            fullWidth
                                                            value={newPlaceData[fieldName]}
                                                            onChange={(event) =>
                                                                setNewPlaceData((prev) => ({ ...prev, [fieldName]: event.target.value }))
                                                            }
                                                            sx={{ mb: 2 }}
                                                        />
                                                    )
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdatePlace} data={newPlaceData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää paikka'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi paikka
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    {fields.map((fieldName) =>
                                        fieldName === 'keskus' ? (
                                            <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                <InputLabel id="addkeskus-label">Keskus</InputLabel>
                                                <Select
                                                    labelId="addkeskusId"
                                                    value={addPlaceData.keskus}
                                                    label="Keskus"
                                                    onChange={(event) =>
                                                        setAddPlaceData((prev) => ({ ...prev, keskus: event.target.value }))
                                                    }
                                                >
                                                    {centers.map((center) => (
                                                        <MenuItem key={center} value={center}>
                                                            {center}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : fieldName === 'maakunta' ? (
                                            <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                <InputLabel id="maakuntaAdd-label">Maakunta</InputLabel>
                                                <Select
                                                    labelId="maakuntaAddId"
                                                    value={addPlaceData.maakunta}
                                                    label="Maakunta"
                                                    onChange={(event) =>
                                                        setAddPlaceData((prev) => ({ ...prev, maakunta: event.target.value }))
                                                    }
                                                >
                                                    {regionNames.map((region) => (
                                                        <MenuItem key={region} value={region}>
                                                            {region}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : fieldName === 'kunta' ? (
                                            <FormControl key={fieldName} sx={{ width: '100%', mb: 2 }}>
                                                <InputLabel id="kuntaAdd-label">Kunta</InputLabel>
                                                <Select
                                                    labelId="kuntaAddId"
                                                    value={addPlaceData.kunta}
                                                    label="Kunta"
                                                    onChange={(event) =>
                                                        setAddPlaceData((prev) => ({ ...prev, kunta: event.target.value }))
                                                    }
                                                >
                                                    {municipalitiesByRegion[addPlaceData.maakunta]?.map((kunta) => (
                                                        <MenuItem key={kunta} value={kunta}>
                                                            {kunta}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <FormTextField
                                                key={fieldName}
                                                fieldName={fieldName}
                                                label={fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
                                                multiline
                                                fullWidth
                                                value={addPlaceData[fieldName]}
                                                onChange={(event) =>
                                                    setAddPlaceData((prev) => ({ ...prev, [fieldName]: event.target.value }))
                                                }
                                                sx={{ mb: 2 }}
                                            />
                                        )
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääPlace(addPlaceData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredPlaces.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredPlaces}
                            array={places}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default PlacesList;